import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Bot, Eye, EyeOff, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { AgentChatSheet } from "../components/ai/AgentChatSheet";
import { PerformanceModelEditor } from "../components/performance/PerformanceModelEditor";
import { PublishModelDialog } from "../components/sportConfig/PublishModelDialog";
import {
	modelOwnerText,
	modelTitle,
} from "../components/sportConfig/modelLabels";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchAPI } from "../lib/utils";

/**
 * Un modello prestativo aperto dalla sezione «Sport e modelli».
 *
 * Il proprio modello si modifica, si rinomina, si pubblica e si ritira; quello
 * di sistema e quelli del team si leggono (o, per l'admin della disciplina, si
 * modificano). L'editor e' lo stesso del pannello admin.
 */
export default function SportModelEditor() {
	const { modelId } = useParams();
	const { user } = useAuth();
	const { t } = useLanguage();
	const [model, setModel] = useState(null);
	const [loading, setLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);
	const [name, setName] = useState("");
	const [publishOpen, setPublishOpen] = useState(false);
	const [withdrawing, setWithdrawing] = useState(false);
	const [assistantOpen, setAssistantOpen] = useState(false);
	// Dopo una scrittura dell'assistente l'editor rilegge domini ed esercizi.
	const [editorVersion, setEditorVersion] = useState(0);

	const isAdmin = user?.is_admin || user?.role === "admin";
	const isCoach = user?.role === "coach" || isAdmin;

	const load = useCallback(async () => {
		try {
			const data = await fetchAPI(`/performance-models/${encodeURIComponent(modelId)}`);
			setModel(data);
			setName(data?.name || "");
			setNotFound(false);
		} catch {
			setNotFound(true);
		} finally {
			setLoading(false);
		}
	}, [modelId]);

	useEffect(() => {
		load();
	}, [load]);

	if (!isCoach) return <Navigate to="/dashboard" replace />;

	const isOwnPersonal = model && !model.is_system && model.can_edit;

	const saveName = async () => {
		const next = name.trim();
		if (!next || next === model?.name) {
			setName(model?.name || "");
			return;
		}
		try {
			const updated = await fetchAPI(
				`/performance-models/${encodeURIComponent(model.model_id)}`,
				{ method: "PUT", body: JSON.stringify({ name: next }) },
			);
			setModel((prev) => ({ ...prev, ...updated }));
			toast.success(t("scModelRenamed"));
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
			setName(model?.name || "");
		}
	};

	const withdraw = async () => {
		if (!window.confirm(t("scConfirmWithdraw"))) return;
		setWithdrawing(true);
		try {
			const updated = await fetchAPI(
				`/performance-models/${encodeURIComponent(model.model_id)}`,
				{ method: "PUT", body: JSON.stringify({ is_published: false }) },
			);
			setModel((prev) => ({ ...prev, ...updated }));
			toast.success(t("scModelWithdrawn"));
		} catch (error) {
			toast.error(`${t("error")}: ${error?.message || ""}`);
		} finally {
			setWithdrawing(false);
		}
	};

	return (
		<Layout>
			<div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 space-y-4">
				<Link
					to="/sport-config"
					className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
				>
					<ArrowLeft className="w-4 h-4" />
					{t("scTitle")}
				</Link>

				{loading ? (
					<div className="flex items-center justify-center h-64 text-muted-foreground">
						<Loader2 className="w-5 h-5 animate-spin mr-2" />
						{t("loading")}
					</div>
				) : notFound || !model ? (
					<p className="text-sm text-muted-foreground">{t("scModelNotFound")}</p>
				) : (
					<>
						<div className="flex flex-wrap items-start justify-between gap-3">
							<div className="min-w-0 space-y-1">
								<p className="text-xs uppercase tracking-wider text-muted-foreground">
									{model.sport_label || model.sport}
								</p>
								{isOwnPersonal ? (
									<Input
										value={name}
										onChange={(event) => setName(event.target.value)}
										onBlur={saveName}
										onKeyDown={(event) => {
											if (event.key === "Enter") event.currentTarget.blur();
										}}
										aria-label={t("scModelName")}
										className="h-10 max-w-md font-heading text-xl bg-transparent border-transparent hover:border-border focus:border-border rounded-sm px-2 -mx-2"
									/>
								) : (
									<h1 className="font-heading text-2xl">{modelTitle(model, t)}</h1>
								)}
								<div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
									<span>{modelOwnerText(model, t)}</span>
									{!model.is_system && (
										<Badge
											className={`border-0 ${
												model.is_published
													? "bg-green-500/20 text-green-400"
													: "bg-secondary text-muted-foreground"
											}`}
										>
											{model.is_published ? t("scPublished") : t("scNotPublished")}
										</Badge>
									)}
								</div>
							</div>
							<div className="flex flex-wrap items-center gap-2">
							{/* L'assistente legge sempre; scrive solo se il modello e'
							    modificabile, e solo su questo (§9octodecies della spec AI). */}
							<Button
								variant="outline"
								className="rounded-sm border-volt/40 text-volt hover:bg-volt/10"
								onClick={() => setAssistantOpen(true)}
								data-testid="model-ai-btn"
							>
								<Bot className="w-4 h-4 sm:mr-1.5" />
								<span className="hidden sm:inline">
									{model.can_edit ? t("scAskAssistant") : t("scAskAssistantRead")}
								</span>
							</Button>
							{isOwnPersonal &&
								(model.is_published ? (
									<Button
										variant="outline"
										className="rounded-sm"
										disabled={withdrawing}
										onClick={withdraw}
									>
										<EyeOff className="w-4 h-4 mr-1.5" />
										{t("scWithdraw")}
									</Button>
								) : (
									<Button
										className="bg-volt hover:bg-volt/90 rounded-sm"
										onClick={() => setPublishOpen(true)}
									>
										<Eye className="w-4 h-4 mr-1.5" />
										{t("scPublish")}
									</Button>
								))}
							</div>
						</div>

						{!model.can_edit && (
							<div className="flex items-start gap-2 rounded-sm border border-border bg-secondary/40 p-3 text-sm text-muted-foreground">
								<Lock className="w-4 h-4 shrink-0 mt-0.5" />
								<span>
									{model.is_system ? t("scReadOnlySystem") : t("scReadOnlyTeam")}
								</span>
							</div>
						)}
						{isOwnPersonal && !model.is_published && (
							<p className="text-sm text-muted-foreground">{t("scDraftHint")}</p>
						)}

						<PerformanceModelEditor
							key={`${model.model_id}_${editorVersion}`}
							model={model}
							canEdit={!!model.can_edit}
							sportIsShooting={!!model.sport_is_shooting}
							onModelChanged={(updated) => setModel((prev) => ({ ...prev, ...updated }))}
						/>

						<AgentChatSheet
							open={assistantOpen}
							onOpenChange={setAssistantOpen}
							title={t("scAssistantTitle").replace("{model}", modelTitle(model, t))}
							description={
								model.can_edit ? t("scAssistantDescription") : t("scAssistantDescriptionRead")
							}
							modelId={model.model_id}
							onModelsChanged={() => {
								load();
								setEditorVersion((version) => version + 1);
							}}
						/>

						<PublishModelDialog
							model={model}
							open={publishOpen}
							onOpenChange={setPublishOpen}
							onPublished={(updated) => setModel((prev) => ({ ...prev, ...updated }))}
						/>
					</>
				)}
			</div>
		</Layout>
	);
}
